import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import "./LocationModal.css";
import { MdMyLocation } from "react-icons/md";
import { FaSearchLocation } from "react-icons/fa";

const recentDefaults = [
    "Mumbai, India",
    "Vipin Garden Extension, Laxmi Vihar",
    "Noida, India",
    "Sector 18, Noida, Uttar Pradesh, India",
];

const STORAGE_KEY = "recentLocations";

// Helper: get a specific type from Google's address components
const getComponent = (components, type) =>
    components.find((c) => c.types.includes(type))?.long_name || "";

// Turn a geocode **result object** into area + city label
const parseAreaCity = (result) => {
    const c = result?.address_components || [];

    const area =
        getComponent(c, "sublocality_level_1") ||
        getComponent(c, "neighborhood") ||
        getComponent(c, "route") ||
        "";

    const city =
        getComponent(c, "locality") ||
        getComponent(c, "administrative_area_level_2") ||
        getComponent(c, "administrative_area_level_1") ||
        "";

    return {
        display: [area, city].filter(Boolean).join(", "),
        full: result?.formatted_address || "",
    };
};



const LocationModal = ({ isOpen, onClose, onSelectLocation }) => {
    const [recentLocations, setRecentLocations] = useState(recentDefaults);
    const [detecting, setDetecting] = useState(false);

    const [query, setQuery] = useState("");
    const [searching, setSearching] = useState(false);
    const [results, setResults] = useState([]); // [{display, full, lat, lng, placeId}]


    useEffect(() => {
        try {
            const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
            setRecentLocations(Array.isArray(stored) && stored.length ? stored : recentDefaults);
        } catch {
            setRecentLocations(recentDefaults);
        }
    }, []);

    const updateRecentLocations = (newLabel) => {
        if (typeof newLabel !== "string" || !newLabel.trim()) return;
        const label = newLabel.trim();
        const updated = [label, ...recentLocations.filter((l) => l !== label)].slice(0, 5);
        setRecentLocations(updated);
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        } catch {/* ignore quota errors */ }
    };

    // Forward geocode: name/address -> lat/lng + nice label
    const geocodeByName = async (text) => {
        const q = text?.trim();
        if (!q) return [];

        const apiKey = "AIzaSyDJU4XzSrUxnEMVk2a7eplTA4spBU3qdO8";
        const url =
            `https://maps.googleapis.com/maps/api/geocode/json` +
            `?address=${encodeURIComponent(q)}` +
            `&key=${apiKey}` +
            `&region=IN&components=country:IN`; // bias/limit to India (optional)

        const res = await fetch(url);
        const data = await res.json();
        if (data.status !== "OK" || !data.results?.length) return [];

        // Map top 5 results into a simple list
        return data.results.slice(0, 5).map(r => {
            const { lat, lng } = r.geometry.location;
            const { display, full } = parseAreaCity(r); // your helper from earlier
            return { display, full, lat, lng, placeId: r.place_id };
        });
    };

    const handleSearch = async () => {
        if (!query.trim()) return;
        setSearching(true);
        try {
            const items = await geocodeByName(query);
            setResults(items);
        } finally {
            setSearching(false);
        }
    };

    // When user picks a result
    const handlePick = (item) => {
        // Persist globally if you want (simple localStorage for now)
        try {
            localStorage.setItem(
                "userLocation",
                JSON.stringify({
                    display: item.display,
                    full: item.full,
                    latitude: item.lat,
                    longitude: item.lng,
                    updatedAt: Date.now(),
                })
            );
        } catch { }

        // Your existing flows
        onSelectLocation(item.display);          // parent still expects a string
        updateRecentLocations(item.display);     // keep recents simple as labels
        onClose();
    };



    // Fetch, then return {display, full}
    const getAddressFromLatLng = async (lat, lng) => {
        // NOTE: don’t hardcode secrets in client apps. For demo only:
        const apiKey = "AIzaSyDJU4XzSrUxnEMVk2a7eplTA4spBU3qdO8";
        const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}&result_type=street_address|sublocality|locality`;

        try {
            const response = await fetch(url);
            const data = await response.json();

            if (data.status !== "OK" || !data.results?.length) return null;

            // Prefer a more precise result if present
            const result =
                data.results.find((r) => r.types.includes("street_address") || r.types.includes("premise")) ||
                data.results[0];

            return parseAreaCity(result); // -> {display, full}
        } catch (error) {
            console.error("Error fetching geocode data:", error);
            return null;
        }
    };

    /// Get user's current location
    const getCurrentLocation = useCallback(() => {
        return new Promise((resolve, reject) => {
            if (!("geolocation" in navigator)) {
                reject(new Error("Geolocation is not available in this browser."));
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    resolve({
                        latitude: latitude.toString(),
                        longitude: longitude.toString(),
                    });
                },
                (error) => {
                    reject(error);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 300000, // 5 minutes
                }
            );
        });
    }, []);

    // Handle "Detect current location" button click
    const handleDetectLocation = async () => {
        if (!("geolocation" in navigator)) {
            alert("Geolocation not supported");
            return;
        }

        setDetecting(true);

        try {
            // Step 1: Get coordinates
            const coords = await getCurrentLocation();

            // Step 2: Reverse-geocode to area + city
            const parsed = await getAddressFromLatLng(coords.latitude, coords.longitude);

            // Step 3: Use only the string label for selection & storage
            if (parsed && parsed.display) {
                onSelectLocation(parsed.display);
                updateRecentLocations(parsed.display);
                onClose();
            } else {
                alert("Failed to detect address from location.");
            }
        } catch (err) {
            console.error("Error detecting location:", err);
            alert(err.message || "Could not detect your location.");
        } finally {
            setDetecting(false);
        }
    };


    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="pw-location-modal-backdrop">
            <div className="pw-location-modal-container">
                <div className="pw-location-modal-header">
                    <h3>Select Your Location</h3>
                    <button onClick={onClose} className="pw-location-modal-close">✖</button>
                </div>

                <div className="pw-location-modal-body">
                    <div className="pw-location-detect">
                        <button
                            onClick={handleDetectLocation}
                            className="pw-location-detect-btn"
                            disabled={detecting}
                        >
                            <MdMyLocation /> {detecting ? "Detecting..." : "Detect current location"}
                        </button>
                        <span>Using GPS</span>
                    </div>

                    {/* <div className="pw-location-search">
                        <input
                            type="text"
                            placeholder="Search location"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleSearch();
                            }}
                        />
                        <button
                            type="button"
                            onClick={handleSearch}
                            disabled={!query.trim() || searching}
                            className="pw-location-search-btn"
                        >
                            {searching ? "Searching..." : "Search"}
                        </button>
                    </div>
                    {results.length > 0 && (
                        <ul className="pw-location-search-results">
                            {results.map((r, i) => (
                                <li
                                    key={`${r.placeId}-${i}`}
                                    className="pw-location-result"
                                    onClick={() => handlePick(r)}
                                >
                                    <FaSearchLocation /> {r.display || r.full}
                                </li>
                            ))}
                        </ul>
                    )} */}

                    <h4 className="pw-location-modal-subheading">Recent Locations</h4>
                    <ul className="pw-location-list">
                        {recentLocations.map((label, idx) =>
                            typeof label === "string" ? (
                                <li
                                    key={`${label}-${idx}`}
                                    className="pw-location-item"
                                    onClick={() => {
                                        onSelectLocation(label);
                                        updateRecentLocations(label);
                                        onClose();
                                    }}
                                >
                                    <FaSearchLocation /> {label.length > 50 ? `${label.slice(0, 50)}…` : label}
                                </li>
                            ) : null
                        )}
                    </ul>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default LocationModal;
